<template>
  <div class="skeleton">
    <header class="top">
      <img class="logo" :src="logoUrl" alt="Infologic"/>
      <span class="ver">v{{ version }}</span>
    </header>

    <div class="splash">
      <div class="center">
        <div class="progress" role="progressbar" :aria-valuenow="Math.round(progressPercent)" aria-valuemin="0" aria-valuemax="100">
          <div class="fill" :style="{ width: progressPercent + '%' }"/>
        </div>
        <div class="status">
          <div class="h">{{ phaseHeading }}</div>
          <div v-if="phaseDetail" class="d">{{ phaseDetail }}</div>
        </div>
      </div>

      <footer class="foot">
        <span class="device">Device {{ deviceLabel }}</span>
        <span>{{ footStatus }}</span>
      </footer>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed} from 'vue';
import useStore from '../store.ts';
import {ConnectionState} from '../socket/ConnectionState.ts';
import {getStoredDeviceId} from '../utils/DeviceId.ts';
import {useLoadingPhase} from '../utils/useLoadingPhase.ts';
import {version} from '../../package.json';
import logoUrl from '../assets/infologic-logo.svg';

const store = useStore();
// see docs/EXTRACTED_COMMENTS.md#c052
const {phases, activeIndex, progressPercent} = useLoadingPhase();

const deviceLabel = computed<string>(() => getStoredDeviceId() || 'Unregistered');

const activeName = computed<string>(() => phases[activeIndex.value]?.name ?? '');

// One honest status line, mapped from the active boot phase.
const phaseHeading = computed<string>(() => {
  if (store.loaded) return 'Display online';
  switch (activeName.value) {
    case 'CONNECT':      return 'Connecting';
    case 'AUTHENTICATE': return 'Authenticating';
    case 'DISCOVER':     return store.layoutUnresolved ? 'Could not resolve layout' : 'Resolving layout';
    case 'BUILD':        return 'Building layout';
    case 'INGEST':       return 'Syncing channels';
    case 'PAINT':        return 'Rendering display';
    case 'PROVISION':    return 'Loading assets';
    case 'READY':        return 'Finishing up';
    default:             return 'Loading';
  }
});

// Real, tabular counts for the active phase — never decorative.
const ingestTotals = computed<{rows: number; exp: number}>(() => {
  const chs = store.loadingDiagnostics?.ingest?.channels ?? [];
  let rows = 0, exp = 0;
  for (const c of chs) {
    rows += c.rows;
    exp += Math.max(0, c.expectedRows);
  }
  return {rows, exp};
});

const layoutSuffix = computed<string>(() => store.layout != null ? ` · layout #${store.layout}` : '');

const phaseDetail = computed<string>(() => {
  const d = store.loadingDiagnostics;
  if (store.loaded) return store.layout != null ? `Layout #${store.layout}` : '';
  switch (activeName.value) {
    case 'INGEST': {
      const {rows, exp} = ingestTotals.value;
      if (exp > 0) return `${rows.toLocaleString()} of ${exp.toLocaleString()} rows${layoutSuffix.value}`;
      return `Receiving channel data${layoutSuffix.value}`;
    }
    case 'PAINT':
      if (d?.paint && d.paint.total > 0) return `${d.paint.painted.toLocaleString()} of ${d.paint.total.toLocaleString()} elements${layoutSuffix.value}`;
      return `Computing geometry${layoutSuffix.value}`;
    case 'PROVISION':
      if (d?.provision) return `Fonts ${d.provision.fontsReady}/${d.provision.fontsTotal} · datasheets ${d.provision.datasheetsBuilt}/${d.provision.datasheetsTotal}`;
      return `Loading assets${layoutSuffix.value}`;
    default:
      return store.layout != null ? `Layout #${store.layout}` : 'Establishing connection';
  }
});

const footStatus = computed<string>(() => {
  switch (store.connection.state) {
    case ConnectionState.Ready:               return store.loaded ? 'Connected · live' : 'Connected · receiving';
    case ConnectionState.Syncing:             return 'Connected · syncing';
    case ConnectionState.Authenticating:      return 'Authenticating';
    case ConnectionState.Connecting:
    case ConnectionState.ConnectedUnverified: return 'Connecting';
    case ConnectionState.Degraded:            return 'Recovering';
    default:                                  return 'Connecting';
  }
});
</script>

<style scoped>
.skeleton {
  inset: 0;
  background: var(--bg);
  color: var(--ink);
  font-family: var(--brand);
  -webkit-font-smoothing: antialiased;
  display: flex;
  align-items: stretch;
  justify-content: center;
}

.splash {
  /* see docs/EXTRACTED_COMMENTS.md#c310 */
  --col: 320px;
  --col-max: 70vw;
  width: 100%;
  max-width: 880px;
  display: grid;
  grid-template-rows: 1fr auto;
}

/* see docs/EXTRACTED_COMMENTS.md#c311 */
.top {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 16px;
}

.logo {
  width: 120px;
  height: 34px;
  object-fit: contain;
  object-position: left center;
}

.ver {
  font-family: var(--mono);
  font-size: 12px;
  color: var(--ink-4);
}

.center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 26px;
  padding: 12px 0;
}

.progress {
  width: var(--col);
  max-width: var(--col-max);
  height: 4px;
  border-radius: 999px;
  background: var(--border);
  overflow: hidden;
}

.progress .fill {
  height: 100%;
  background: var(--primary);
  border-radius: 999px;
  transition: width 600ms var(--ease);
}

.status {
  text-align: center;
}

.status .h {
  font-size: 1.25rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--ink);
}

.status .d {
  font-size: 13.5px;
  font-weight: 300;
  color: var(--ink-3);
  margin-top: 6px;
  font-variant-numeric: tabular-nums;
}

.foot {
  width: var(--col);
  max-width: var(--col-max);
  margin: 0 auto;
  padding: 18px 0;
  border-top: 1px solid var(--border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: var(--ink-4);
}

.foot .device {
  font-variant-numeric: tabular-nums;
}

@media (prefers-reduced-motion: reduce) {
  .progress .fill {
    transition: none;
  }
}
</style>
